import { useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "./authentication/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import QRCode from "qrcode.react";

// Constants for cleaner code
const SECTION_TYPES = [
  { value: "TABLE", label: "🪑 Table" },
  { value: "ROOM", label: "🏠 Room" },
  { value: "OTHER", label: "📍 Other" },
];

const createEmptySection = (type = "TABLE") => ({
  name: "",
  type,
  roomType: type === "ROOM" ? "AC" : null,
  totalUnits: 1
});

const emptyFloor = { floorName: "", sections: [] };

const AdminLayoutPage = () => {
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const { role, loading: authLoading, user } = useAuth();

  const [showQrModal, setShowQrModal] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [selectedUnitForPrint, setSelectedUnitForPrint] = useState(null);
  const slug = user?.slug;

  useEffect(() => {
    const fetchStore = async () => {
      if (slug) {
        try {
          const res = await api.get(`/api/public/restaurant/${slug}`);
          setStoreInfo(res.data);
        } catch (err) {
          console.error("Failed to load restaurant details for QR", err);
        }
      }
    };
    if (showQrModal) {
      fetchStore();
    }
  }, [slug, showQrModal]);

  useEffect(() => {
    if (!authLoading && Number(role) === 1) loadLayout();
  }, [authLoading, role]);

  const loadLayout = async () => {
    try {
      const res = await api.get("/layout");
      if (res.data?.floors) setFloors(res.data.floors);
    } catch (err) {
      console.error("New layout required");
    }
  };

  // State Handlers
  const addFloor = () =>
    setFloors([...floors, { ...emptyFloor, sections: [createEmptySection()] }]);
  const removeFloor = (i) => setFloors(floors.filter((_, idx) => idx !== i));
  const updateFloorName = (i, val) => {
    const copy = [...floors];
    copy[i].floorName = val;
    setFloors(copy);
  };

  const addSection = (fIdx) => {
    const copy = [...floors];
    copy[fIdx].sections.push(createEmptySection());
    setFloors(copy);
  };

const updateSection = (fIdx, sIdx, field, value) => {
  const copy = [...floors];
  copy[fIdx].sections[sIdx][field] = value;

  if (field === "type") {
    if (value === "ROOM") {
      copy[fIdx].sections[sIdx].roomType = "AC";
    } else {
      copy[fIdx].sections[sIdx].roomType = null;
    }
  }

  setFloors(copy);
};


  const removeSection = (fIdx, sIdx) => {
    const copy = [...floors];
    copy[fIdx].sections.splice(sIdx, 1);
    setFloors(copy);
  };

  const saveLayout = async () => {
    if (!floors.length) return toast.error("Add at least one floor");

    // Validate floor and section fields before API request
    for (let fIdx = 0; fIdx < floors.length; fIdx++) {
      const floor = floors[fIdx];
      if (!floor.floorName || !floor.floorName.trim()) {
        return toast.error(`Floor #${fIdx + 1} name is required`);
      }
      
      if (!floor.sections || !floor.sections.length) {
        return toast.error(`Floor "${floor.floorName}" must have at least one section`);
      }
      
      for (let sIdx = 0; sIdx < floor.sections.length; sIdx++) {
        const section = floor.sections[sIdx];
        if (!section.name || !section.name.trim()) {
          return toast.error(`Section #${sIdx + 1} name is required on floor "${floor.floorName}"`);
        }
        if (!section.type) {
          return toast.error(`Section "${section.name}" on floor "${floor.floorName}" must have a type selected`);
        }
        if (!section.totalUnits || section.totalUnits < 1) {
          return toast.error(`Section "${section.name}" on floor "${floor.floorName}" must have at least 1 unit`);
        }
      }
    }

    try {
      setLoading(true);
      await api.post("/layout", { floors });
      toast.success("Layout saved successfully!");
    } catch (err) {
      const errMsg = err.response?.data?.message || err.response?.data?.error || "Failed to save layout";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const getAllUnits = () => {
    const unitsList = [];
    floors.forEach((floor) => {
      floor.sections.forEach((section) => {
        for (let i = 1; i <= (section.totalUnits || 0); i++) {
          unitsList.push({
            floorName: floor.floorName,
            sectionName: section.name,
            unitNumber: i,
            sectionType: section.type,
            roomType: section.roomType || "",
          });
        }
      });
    });
    return unitsList;
  };

  const handlePrint = (unit) => {
    setSelectedUnitForPrint(unit);
    setTimeout(() => {
      window.print();
    }, 300);
  };

  if (authLoading)
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        Loading Configuration...
      </div>
    );
  if (Number(role) !== 1)
    return <div className="p-10 text-center text-red-500">Access Denied</div>;

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 mb-8 shadow-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800">
              Restaurant Architecture
            </h1>
            <p className="text-sm text-slate-500">
              Define floors, dining areas, and private rooms
            </p>
          </div>
          <div className="flex items-center gap-3">
            {floors.length > 0 && (
              <button
                onClick={() => setShowQrModal(true)}
                className="px-6 py-2.5 rounded-full font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-all transform active:scale-95 flex items-center gap-2"
              >
                <span>🖨️</span> Print Table QRs
              </button>
            )}
            <button
              onClick={saveLayout}
              disabled={loading}
              className={`px-8 py-2.5 rounded-full font-bold text-white transition-all transform active:scale-95 ${
                loading
                  ? "bg-gray-400"
                  : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg"
              }`}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 space-y-8">
        {/* Floors Loop */}
        {floors.map((floor, fIdx) => (
          <div
            key={fIdx}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
          >
            {/* Floor Header */}
            <div className="bg-slate-50 px-6 py-4 flex items-center gap-4 border-b">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg font-bold">
                {fIdx + 1}
              </span>
              <input
                className="bg-transparent text-lg font-semibold focus:outline-none focus:border-b border-indigo-500 flex-1"
                placeholder="Floor Name (e.g., Rooftop Terrace)"
                value={floor.floorName}
                onChange={(e) => updateFloorName(fIdx, e.target.value)}
              />
              <button
                onClick={() => removeFloor(fIdx)}
                className="px-4 py-1.5 text-sm font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 hover:text-red-700 transition-all duration-200 shadow-sm hover:shadow"
              >
                🗑 Delete
              </button>
            </div>

            {/* Sections Area */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-12 gap-4 mb-2 text-xs font-bold text-slate-400 uppercase tracking-wider px-2">
                <div className="col-span-4">Section Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Sub-Type</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2"></div>
              </div>

              {floor.sections.map((section, sIdx) => (
                <div
                  key={sIdx}
                  className="grid grid-cols-12 gap-4 items-center bg-white p-2 rounded-xl border border-transparent hover:border-slate-200 hover:shadow-sm transition"
                >
                  <div className="col-span-4">
                    <input
                      className="w-full bg-slate-50 border-none rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Window Side"
                      value={section.name}
                      onChange={(e) =>
                        updateSection(fIdx, sIdx, "name", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-span-2">
                    <select
                      className="w-full bg-slate-50 border-none rounded-lg px-2 py-2 text-sm appearance-none cursor-pointer"
                      value={section.type}
                      onChange={(e) =>
                        updateSection(fIdx, sIdx, "type", e.target.value)
                      }
                    >
                      {SECTION_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    {section.type === "ROOM" ? (
                      <select
                        className="w-full bg-indigo-50 text-indigo-700 border-none rounded-lg px-2 py-2 text-sm font-medium"
                        value={section.roomType || ""}
                        onChange={(e) =>
                          updateSection(fIdx, sIdx, "roomType", e.target.value)
                        }
                      >
                        <option value="">Select...</option>
                        <option value="AC">AC</option>
                        <option value="NON_AC">Non-AC</option>
                      </select>
                    ) : (
                      <div className="h-8 bg-slate-100/50 rounded-lg" />
                    )}
                  </div>

                  <div className="col-span-2">
                    <input
                      type="number"
                      className="w-full text-center bg-slate-50 border-none rounded-lg py-2 text-sm font-bold"
                      value={section.totalUnits}
                      onChange={(e) =>
                        updateSection(
                          fIdx,
                          sIdx,
                          "totalUnits",
                          Number(e.target.value),
                        )
                      }
                    />
                  </div>

                  <div className="col-span-2 text-right px-2">
                    <button
                      onClick={() => removeSection(fIdx, sIdx)}
                      className="flex items-center justify-center w-9 h-9 bg-red-50 text-red-500 rounded-full hover:bg-red-100 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={() => addSection(fIdx)}
                className="w-full py-3 mt-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition flex justify-center items-center gap-2 text-sm font-medium"
              >
                + Add New Section
              </button>
            </div>
          </div>
        ))}

        {/* Empty State / Add Floor */}
        <div className="pt-4 flex flex-col items-center">
          {!floors.length ? (
            <div className="text-center py-20 bg-white w-full rounded-3xl border-2 border-dashed border-slate-200">
              <div className="text-5xl mb-4">🏬</div>
              <h3 className="text-lg font-bold text-slate-700">
                No Floors Defined
              </h3>
              <p className="text-slate-500 mb-6">
                Start by adding the main dining area or ground floor.
              </p>
              <button
                onClick={addFloor}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-md"
              >
                Add My First Floor
              </button>
            </div>
          ) : (
            <button
              onClick={addFloor}
              className="group flex items-center gap-3 px-8 py-4 bg-white border-2 border-slate-200 rounded-2xl text-slate-600 font-bold hover:border-indigo-500 hover:text-indigo-600 transition shadow-sm"
            >
              <span className="text-xl group-hover:scale-125 transition-transform">
                +
              </span>
              Add Another Floor
            </button>
          )}
        </div>
      </div>

      {/* Table QR Modal */}
      {showQrModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-slideInUp">
            
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Print Table QR Codes</h2>
                <p className="text-xs text-slate-500 mt-1">Generate table-specific locks to enable smart self-ordering</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePrint("ALL")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2 rounded-xl text-sm transition-all"
                >
                  🖨️ Print All Cards
                </button>
                <button
                  onClick={() => setShowQrModal(false)}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold px-4 py-2 rounded-xl text-sm transition-all"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50">
              {floors.length === 0 ? (
                <p className="text-center text-slate-500 py-10">No floors defined to generate QRs from.</p>
              ) : (
                <div className="space-y-8">
                  {floors.map((floor, fIdx) => (
                    <div key={fIdx} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2 text-indigo-600">
                        {floor.floorName || `Floor #${fIdx + 1}`}
                      </h3>

                      {floor.sections.map((section, sIdx) => (
                        <div key={sIdx} className="mb-6 last:mb-0">
                          <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                            <span>{section.name || `Section #${sIdx + 1}`}</span>
                            <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded-full">
                              {section.type} {section.roomType ? `(${section.roomType})` : ""}
                            </span>
                          </h4>

                          {/* Units Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {Array.from({ length: section.totalUnits || 0 }).map((_, uIdx) => {
                              const unitNumber = uIdx + 1;
                              const qrUrl = `${window.location.origin}/?qr=true&slug=${slug}&floorName=${encodeURIComponent(floor.floorName)}&sectionName=${encodeURIComponent(section.name)}&unitNumber=${unitNumber}&sectionType=${section.type}&roomType=${section.roomType || ""}#/r/${slug}?floorName=${encodeURIComponent(floor.floorName)}&sectionName=${encodeURIComponent(section.name)}&unitNumber=${unitNumber}&sectionType=${section.type}&roomType=${section.roomType || ""}`;

                              return (
                                <div key={uIdx} className="border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-between bg-slate-50/30 hover:shadow-md transition duration-200">
                                  <div className="text-center mb-3">
                                    <span className="text-xs text-slate-400 block uppercase font-bold tracking-wider">
                                      {section.type === "TABLE" ? "Table" : "Room"}
                                    </span>
                                    <span className="text-xl font-extrabold text-slate-800">
                                      {section.type === "TABLE" ? "T" : "R"}{unitNumber}
                                    </span>
                                  </div>

                                  <div className="p-2 bg-white rounded-lg border shadow-sm mb-3">
                                    <QRCode value={qrUrl} size={90} />
                                  </div>

                                  <button
                                    onClick={() => handlePrint({
                                      floorName: floor.floorName,
                                      sectionName: section.name,
                                      unitNumber,
                                      sectionType: section.type,
                                      roomType: section.roomType || "",
                                    })}
                                    className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-1.5 rounded-lg text-xs transition-all"
                                  >
                                    🖨️ Print Card
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden Printable Container */}
      <div id="printable-qr-area" className="hidden">
        <style>
          {`
            @media print {
              body * {
                visibility: hidden;
              }
              #printable-qr-area, #printable-qr-area * {
                visibility: visible;
              }
              #printable-qr-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                display: block !important;
                background: white !important;
              }
              .print-card-page {
                page-break-after: always;
                border: 2px solid #334155 !important;
                border-radius: 24px !important;
                padding: 40px !important;
                width: 380px !important;
                height: 520px !important;
                margin: 40px auto !important;
                text-align: center !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: space-between !important;
                align-items: center !important;
                box-sizing: border-box !important;
                font-family: system-ui, -apple-system, sans-serif !important;
              }
              .print-card-title {
                font-size: 24px !important;
                font-weight: 800 !important;
                color: #1e293b !important;
                margin-top: 8px !important;
              }
              .print-card-logo {
                width: 70px !important;
                height: 70px !important;
                border-radius: 9999px !important;
                object-fit: contain !important;
                background-color: white !important;
              }
              .print-card-info-box {
                margin: 20px 0 !important;
                padding: 15px !important;
                background-color: #f8fafc !important;
                border: 2px dashed #cbd5e1 !important;
                border-radius: 16px !important;
                width: 100% !important;
                box-sizing: border-box !important;
              }
              .print-card-table-num {
                font-size: 36px !important;
                font-weight: 900 !important;
                color: #4f46e5 !important;
                margin-top: 4px !important;
                display: block !important;
              }
              .print-card-instruction {
                font-size: 14px !important;
                font-weight: 700 !important;
                color: #64748b !important;
                text-transform: uppercase !important;
                letter-spacing: 0.05em !important;
                display: block !important;
              }
              .print-card-subinfo {
                font-size: 11px !important;
                color: #94a3b8 !important;
                display: block !important;
                margin-top: 4px !important;
              }
              .print-card-qr-box {
                padding: 12px !important;
                background: white !important;
                border: 1px solid #e2e8f0 !important;
                border-radius: 12px !important;
                display: inline-block !important;
              }
              .print-card-footer {
                font-size: 10px !important;
                color: #94a3b8 !important;
              }
            }
          `}
        </style>

        {selectedUnitForPrint === "ALL" ? (
          getAllUnits().map((unit, idx) => {
            const qrUrl = `${window.location.origin}/?qr=true&slug=${slug}&floorName=${encodeURIComponent(unit.floorName)}&sectionName=${encodeURIComponent(unit.sectionName)}&unitNumber=${unit.unitNumber}&sectionType=${unit.sectionType}&roomType=${unit.roomType || ""}#/r/${slug}?floorName=${encodeURIComponent(unit.floorName)}&sectionName=${encodeURIComponent(unit.sectionName)}&unitNumber=${unit.unitNumber}&sectionType=${unit.sectionType}&roomType=${unit.roomType || ""}`;

            return (
              <div key={idx} className="print-card-page">
                <div>
                  {storeInfo?.logo && (
                    <img src={storeInfo.logo} className="print-card-logo" alt="logo" />
                  )}
                  <h3 className="print-card-title">{storeInfo?.name || "Cloud Rasoi"}</h3>
                </div>
                
                <div className="print-card-info-box">
                  <span className="print-card-instruction">Scan to Order & Pay</span>
                  <span className="print-card-table-num">
                    {unit.sectionType === "TABLE" ? "Table" : "Room"} {unit.unitNumber}
                  </span>
                  <span className="print-card-subinfo">
                    ({unit.floorName} - {unit.sectionName})
                  </span>
                </div>

                <div className="print-card-qr-box">
                  <QRCode value={qrUrl} size={180} />
                </div>

                <p className="print-card-footer">Powered by CloudRasoi</p>
              </div>
            );
          })
        ) : (
          selectedUnitForPrint && (() => {
            const qrUrl = `${window.location.origin}/?qr=true&slug=${slug}&floorName=${encodeURIComponent(selectedUnitForPrint.floorName)}&sectionName=${encodeURIComponent(selectedUnitForPrint.sectionName)}&unitNumber=${selectedUnitForPrint.unitNumber}&sectionType=${selectedUnitForPrint.sectionType}&roomType=${selectedUnitForPrint.roomType || ""}#/r/${slug}?floorName=${encodeURIComponent(selectedUnitForPrint.floorName)}&sectionName=${encodeURIComponent(selectedUnitForPrint.sectionName)}&unitNumber=${selectedUnitForPrint.unitNumber}&sectionType=${selectedUnitForPrint.sectionType}&roomType=${selectedUnitForPrint.roomType || ""}`;

            return (
              <div className="print-card-page">
                <div>
                  {storeInfo?.logo && (
                    <img src={storeInfo.logo} className="print-card-logo" alt="logo" />
                  )}
                  <h3 className="print-card-title">{storeInfo?.name || "Cloud Rasoi"}</h3>
                </div>
                
                <div className="print-card-info-box">
                  <span className="print-card-instruction">Scan to Order & Pay</span>
                  <span className="print-card-table-num">
                    {selectedUnitForPrint.sectionType === "TABLE" ? "Table" : "Room"} {selectedUnitForPrint.unitNumber}
                  </span>
                  <span className="print-card-subinfo">
                    ({selectedUnitForPrint.floorName} - {selectedUnitForPrint.sectionName})
                  </span>
                </div>

                <div className="print-card-qr-box">
                  <QRCode value={qrUrl} size={180} />
                </div>

                <p className="print-card-footer">Powered by CloudRasoi</p>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default AdminLayoutPage;
